import { db } from './db';

// All operations are wrapped in try/catch so that an IndexedDB failure (quota exceeded,
// corrupted store, Safari private-mode edge case) degrades silently instead of crashing
// the offline fallback path. When cache reads fail the app simply makes a network request.
export const localCache = {
  get: async (key) => {
    try { return (await db.cache.get(key))?.data; }
    catch { return undefined; }
  },
  set: async (key, data) => {
    try { await db.cache.put({ key, data, updated_at: Date.now() }); }
    catch { /* quota exceeded or store unavailable — degrade silently */ }
  },
  delete: async (key) => {
    try { await db.cache.delete(key); }
    catch { /* ignore */ }
  },
  // Scan all list-cache entries whose key starts with keyPrefix, returning the first item
  // whose id matches. Used by detail getters so an item visible in a list is also visible
  // on its detail page without requiring a prior visit to that specific detail page.
  findInLists: async (keyPrefix, id) => {
    try {
      const entries = await db.cache.where('key').startsWith(keyPrefix).toArray();
      for (const entry of entries) {
        if (Array.isArray(entry.data)) {
          const found = entry.data.find(item => item.id === id);
          if (found) return found;
        }
      }
    } catch { /* ignore */ }
    return null;
  },
};

// Remove Dexie cache entries older than maxAgeMs (default 7 days).
// Runs as a full table scan — acceptable for the small cache table (~50–200 rows).
export const pruneCache = async (maxAgeMs = 7 * 24 * 60 * 60 * 1000) => {
  const cutoff = Date.now() - maxAgeMs;
  const staleKeys = await db.cache
    .filter((entry) => !!entry.updated_at && entry.updated_at < cutoff)
    .primaryKeys();
  if (staleKeys.length > 0) await db.cache.bulkDelete(staleKeys);
};

// Remove all db.cache entries whose key contains the given districtId.
// Called when district_id changes mid-session so stale cross-district list caches
// (churches_<id>, visits_district_<id>, families_rank_<id>, etc.) stop being
// served by findInLists fallbacks. Entity-level caches (person_<uuid>, etc.)
// are left intact — they are orphaned but never reached by new-district queries.
export const purgeCacheForDistrict = async (districtId) => {
  if (!districtId) return 0;
  const allKeys = await db.cache.toCollection().primaryKeys();
  const stale = allKeys.filter((k) => String(k).includes(districtId));
  if (stale.length > 0) await db.cache.bulkDelete(stale);
  return stale.length;
};
